import React from 'react';
import { firePhoneClick } from '@/lib/tracking';

/**
 * Canonical phone number for Y3 Smiles Dental in E.164 format.
 *
 * Why E.164? Spaces in tel: URIs are valid but some older Android dialers
 * fail to parse them. Country-prefixed E.164 (`+61...`) is universally
 * supported and routes correctly when users are roaming.
 *
 * Update this in ONE place if the number ever changes.
 */
export const Y3_PHONE_E164 = '+61390224442';
export const Y3_PHONE_DISPLAY = '03 9022 4442';

interface TelLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /**
   * Identifies the page section that fired the click. Required so we can
   * see which CTAs drive calls in GA4 / Ads.
   * Examples: "header_desktop", "header_mobile", "footer", "emergency_hero",
   * "service_location_template", "contact_section", "treatment_card_emergency".
   */
  clickSource: string;

  /**
   * Override the canonical phone number if a specific page legitimately needs
   * a different line. Should be a rare exception. E.164 format only.
   */
  phoneNumber?: string;
}

/**
 * Single source of truth for every tel: link on the site.
 *
 * Behavior:
 *  - Always renders `tel:+61390224442` (or override) so dialers parse cleanly.
 *  - Calls firePhoneClick() from src/lib/tracking which:
 *      • pushes { event: 'phone_click', location, event_id } to GTM dataLayer
 *      • fires Google Ads conversion if AW conversion label is configured in
 *        src/data/tracking.json
 *      • fires Meta Pixel 'Contact' event if pixel is configured
 *  - Preserves any user-supplied onClick.
 *  - Forwards every other anchor prop (className, aria-label, target, etc.).
 */
const TelLink: React.FC<TelLinkProps> = ({
  clickSource,
  phoneNumber = Y3_PHONE_E164,
  onClick,
  children,
  ...anchorProps
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    try {
      firePhoneClick(clickSource);
    } catch {
      // Swallow tracking errors — the dialer must still open.
    }
    onClick?.(e);
  };

  return (
    <a href={`tel:${phoneNumber}`} onClick={handleClick} {...anchorProps}>
      {children}
    </a>
  );
};

export default TelLink;
